import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { todayIso } from '../../shared/utils/iso-date.util';
export interface Invoice {
  id: number;
  customerId: string;
  showReId: number;
  basePrice: number;
  unitPrice: number;
  baseFeeStartDate: string;
  baseFeeEndDate: string;
  serviceFeeStartDate: string;
  serviceFeeEndDate: string;
  creationDate: string;
  printingDate: string | null;
  envelopingDate: string | null;
  /** Credit note (Gutschrift) — credit/regenerate are not offered for these (EB-4396). */
  isCreditNote?: boolean;
  /** Preliminary invoice (test run, no number yet). */
  isTestRun?: boolean;
}

export interface InvoiceGenerationRequest {
  basicFeeFrom: string;
  basicFeeUntil: string;
  serviceFeeFrom: string;
  serviceFeeUntil: string;
}

export interface CustomerSettings {
  mainCustomerLanguage: number | null;
  invoiceDay: number;
  paymentTerm: number;
  usageFeeLimit: number;
  billingEnabled: boolean | null;
  firstBillImmediately: boolean;
  showInvoiceByAccounts: boolean | null;
  pdfBackground: string | null;
  showUnitPrice: boolean;
}

/** Raw /settings endpoint only returns these fields */
interface SettingsApiResponse {
  firstBillImmediately: boolean;
  active: boolean;
  startDate: string;
}

/** Customer object contains the billing fields */
interface CustomerBillingFields {
  mainCustomerLanguage: number | null;
  invoiceDay: number;
  paymentTerm: number;
  usageFeeLimit: number;
  billingEnabled: boolean | null;
  showInvoiceByAccounts: boolean;
  showInvoiceWithUnitPrice: boolean;
  pdfBackground: string | null;
  paymentMethod?: string | null;
  iban?: string | null;
  bic?: string | null;
  accountHolder?: string | null;
  taxId?: string | null;
  vatId?: string | null;
  invoiceEmail?: string | null;
  billingLanguage?: string | null;
  companyName?: string | null;
  contractFullName?: string | null;
  street?: string | null;
  houseNumber?: string | null;
  zipCode?: string | null;
  city?: string | null;
  countryCode?: string | null;
}

/** Billing details — payment + tax + invoice address */
export interface BillingDetails {
  paymentMethod: string | null;
  iban: string | null;
  bic: string | null;
  accountHolder: string | null;
  taxId: string | null;
  vatId: string | null;
  invoiceEmail: string | null;
  billingLanguage: string | null;
  invoiceAddress: string | null;
}

export interface Language {
  id: number;
  name: string;
}

@Injectable({ providedIn: 'root' })
export class InvoiceService {
  private readonly http = inject(HttpClient);

  /** Combine /settings (firstBillImmediately) + customer object (billing fields) */
  getSettings(customerId: number): Observable<CustomerSettings> {
    return forkJoin({
      settings: this.http.get<SettingsApiResponse>(`${environment.contractUrl}/customers/${customerId}/settings`),
      customer: this.http.get<CustomerBillingFields>(`${environment.contractUrl}/customers/${customerId}`),
    }).pipe(
      map(({ settings, customer }) => ({
        firstBillImmediately: settings.firstBillImmediately,
        mainCustomerLanguage: customer.mainCustomerLanguage,
        invoiceDay: customer.invoiceDay,
        paymentTerm: customer.paymentTerm,
        usageFeeLimit: customer.usageFeeLimit,
        billingEnabled: customer.billingEnabled,
        showInvoiceByAccounts: customer.showInvoiceByAccounts,
        pdfBackground: customer.pdfBackground,
        showUnitPrice: customer.showInvoiceWithUnitPrice,
      })),
    );
  }

  /** Fetch billing details (payment, tax, invoice address) from the customer record */
  getBillingDetails(customerId: number): Observable<BillingDetails> {
    return this.http
      .get<CustomerBillingFields>(`${environment.contractUrl}/customers/${customerId}`)
      .pipe(
        map((c) => {
          const street = [c.street, c.houseNumber].filter(Boolean).join(' ').trim();
          const cityLine = [c.zipCode, c.city].filter(Boolean).join(' ').trim();
          const country = c.countryCode ?? '';
          const parts = [c.companyName ?? c.contractFullName, street, cityLine, country]
            .filter((p) => !!p && String(p).trim().length > 0);
          return {
            paymentMethod: c.paymentMethod ?? null,
            iban: c.iban ?? null,
            bic: c.bic ?? null,
            accountHolder: c.accountHolder ?? null,
            taxId: c.taxId ?? null,
            vatId: c.vatId ?? null,
            invoiceEmail: c.invoiceEmail ?? null,
            billingLanguage: c.billingLanguage ?? null,
            invoiceAddress: parts.length > 0 ? parts.join(', ') : null,
          };
        }),
      );
  }

  patchCustomer(customerId: number, changes: Record<string, any>): Observable<any> {
    return this.http.patch(`${environment.contractUrl}/customers/${customerId}`, changes);
  }

  generateInvoice(customerId: number, data: InvoiceGenerationRequest): Observable<any> {
    return this.http.post(`${environment.contractUrl}/customers/${customerId}/plannedInvoices`, data);
  }

  getLanguages(): Observable<Language[]> {
    return this.http.get<Language[]>(`${environment.phoneServiceUrl}/languages`);
  }

  /**
   * Map a raw /invoice/v1 record onto the legacy Invoice shape that the
   * rest of the app expects. The v1 endpoint uses different field names
   * (`invoiceNumber`, `createdOn`, `sum*Fee`, `*From/To`); this helper
   * normalises them so list and by-id responses are interchangeable.
   */
  private mapV1Invoice = (raw: {
    id: number;
    customerId: string;
    invoiceNumber: number;
    sumBasicFee: number;
    sumServiceFee: number;
    basicFeeFrom: string | null;
    basicFeeTo: string | null;
    serviceFeeFrom: string | null;
    serviceFeeTo: string | null;
    createdOn: string;
    isTestRun?: boolean | null;
    isCreditNote?: boolean | null;
  }): Invoice => ({
    id: raw.id,
    customerId: raw.customerId,
    showReId: raw.invoiceNumber,
    basePrice: raw.sumBasicFee,
    unitPrice: raw.sumServiceFee,
    baseFeeStartDate: raw.basicFeeFrom ?? '',
    baseFeeEndDate: raw.basicFeeTo ?? '',
    serviceFeeStartDate: raw.serviceFeeFrom ?? '',
    serviceFeeEndDate: raw.serviceFeeTo ?? '',
    creationDate: raw.createdOn,
    // /invoice/v1 doesn't expose printing/enveloping dates; legacy table
    // renderer treats null as "not yet shipped".
    printingDate: null,
    envelopingDate: null,
    isCreditNote: raw.isCreditNote === true,
    isTestRun: raw.isTestRun === true,
  });

  /** Single-invoice fetch — used to back-fill anything missed by the list. */
  getInvoiceById(invoiceId: number): Observable<Invoice> {
    return this.http
      .get<Parameters<typeof this.mapV1Invoice>[0]>(`${environment.invoiceUrl}/invoices/${invoiceId}`)
      .pipe(map(this.mapV1Invoice));
  }

  /**
   * Fetch all invoices for a customer. We previously used the legacy
   * `${environment.contractUrl}/invoices` endpoint which filters on print status
   * (verified live for customer 78159: returned 120 invoices vs. 212 in
   * the v1 feed — most-recent April rows missing entirely). The v1
   * `searchInvoices` endpoint accepts a `search` query in the format
   * `<field><operator><value>` and returns the complete picture.
   *
   * `size=500` is generous enough for the largest customer histories we
   * see, and the result is NOT paginated further — `totalPages` is ignored.
   *
   * Correction (EB-4170 review, 2026-09-04): the legacy `fromDate` parameter was
   * **not** vestigial, as this comment previously claimed. Probed against test:
   * `GET /contract/v2/rest/invoices?customerId=13279&fromDate=2026-09-04` returns
   * `[]` while `fromDate=2018-01-01` returns 3 rows, and `fromDate=2023-11-01`
   * keeps a row whose period ends 2023-11-26 while dropping one ending
   * 2018-09-12 — so the server filters on `baseFeeEndDate`, inclusively.
   *
   * The page is sorted `basicFeeTo,desc` so the 500-row window is deterministic and
   * current-period-first. Without a sort the server decides which 500 rows a large
   * history yields, and if it defaults to oldest-first the *current* period is the first
   * thing dropped (PR #42 review H1). Callers that reason about invoiced periods should
   * use {@link getCurrentInvoices} instead of filtering this list themselves.
   */
  getInvoices(customerId: number): Observable<Invoice[]> {
    const params = new HttpParams()
      .set('search', `customerId:${customerId}`)
      .set('sort', 'basicFeeTo,desc')
      .set('size', '500');
    return this.http
      .get<{ content: Parameters<typeof this.mapV1Invoice>[0][] }>(
        `${environment.invoiceUrl}/invoices`,
        { params },
      )
      .pipe(map((page) => (page.content ?? []).map(this.mapV1Invoice)));
  }

  /**
   * Invoices whose base-fee period is still running on or after `fromIso` — the rows the
   * contract date rules need, filtered by the server instead of by the client.
   *
   * Why this exists (PR #42 review H1): the date rules used to read the customer's whole
   * history through {@link getInvoices} and apply the cut-off themselves. With more
   * invoices than fit one page the current period could fall outside it, and the rules
   * degrade *open*, not closed — {@link maxInvoiceEndDate} finds no current period,
   * returns `null`, `invoicedPeriodApplies` goes false, and the floor collapses back to
   * today. That is the EB-4174 defect the rules exist to prevent, so the gap could make
   * AMIE looser than old CMT, never stricter.
   *
   * Search syntax (invoice-api OpenAPI, `GET /invoices`): multiple criteria are separated
   * by a **comma**, not a semicolon. Probed on test 2026-09-07 — with `;` the
   * `customerId` term is silently dropped and the response carries `totalElements: 94005`,
   * i.e. every customer's invoices; with `,` the same query for customer 13279 returns
   * `totalElements: 3`. An earlier comment here recommended the `;` form as "verified to
   * work"; it was not.
   *
   * The `>` operator is **inclusive** despite the symbol, so a period ending exactly on
   * `fromIso` is returned — the same boundary the client-side rule uses (`end >= cutoff`),
   * so the two cannot disagree by a day. Measured on test 2026-09-07: customer 13279 with
   * `basicFeeTo>2023-11-26` returns both rows whose period ends on 2023-11-26. The legacy
   * `fromDate` parameter behaves the same way, which is consistent.
   *
   * Callers keep `maxInvoiceEndDate`'s own cut-off because it is the rule old CMT documents
   * and because a caller may pass an unfiltered list. It is **not** protection against the
   * server tightening `>`, as this comment previously argued: that filter only ever removes
   * rows, never restores one the server withheld. If `>` became exclusive, the period ending
   * exactly today would simply be absent, `invoicedPeriodApplies` would go false and the
   * floor would collapse — EB-4174 again, one day wide, on the last day of every invoiced
   * period. The inclusive boundary is measured, not assumed (see above), and it is the
   * assumption to re-probe if this rule ever misbehaves (PR #42 re-review).
   *
   * `sort` is sent but its effect is NOT probed — `invoice.service.spec.ts` asserts the
   * outgoing parameter, not that invoice-api binds it. `basicFeeTo` is nullable and DB2
   * sorts NULLs high, so under `desc` null rows would head the page; the server-side
   * `basicFeeTo>` filter is what actually keeps the current period in view here, not the
   * ordering.
   */
  getCurrentInvoices(customerId: number, fromIso: string = todayIso()): Observable<Invoice[]> {
    const params = new HttpParams()
      .set('search', `customerId:${customerId},basicFeeTo>${fromIso}`)
      .set('sort', 'basicFeeTo,desc')
      .set('size', '500');
    return this.http
      .get<{ content: Parameters<typeof this.mapV1Invoice>[0][] }>(
        `${environment.invoiceUrl}/invoices`,
        { params },
      )
      .pipe(map((page) => (page.content ?? []).map(this.mapV1Invoice)));
  }

  /** Invoice detail PDF */
  getDetailPdfUrl(invoiceId: number): string {
    return `${environment.invoiceUrl}/invoices/${invoiceId}/pdf`;
  }

  /** EVN (Einzelverbindungsnachweis) as PDF */
  getEvnPdfUrl(invoiceId: number): string {
    return `${environment.invoiceUrl}/invoices/${invoiceId}/evn`;
  }

  /** EVN as Excel */
  getEvnExcelUrl(invoiceId: number): string {
    return `${environment.invoiceUrl}/invoices/${invoiceId}/evn?format=xlsx`;
  }

  /** Fetch EVN PDF as blob (auth headers added by interceptor) */
  getEvnPdf(invoiceId: number): Observable<Blob> {
    return this.http.get(`${environment.invoiceUrl}/invoices/${invoiceId}/evn`, { responseType: 'blob' });
  }

  /** Fetch EVN Excel as blob */
  getEvnExcel(invoiceId: number): Observable<Blob> {
    return this.http.get(`${environment.invoiceUrl}/invoices/${invoiceId}/evn`, {
      responseType: 'blob',
      params: { format: 'xlsx' },
    });
  }

  /** Fetch the stored invoice detail PDF as blob (no regeneration). */
  getDetailPdf(invoiceId: number): Observable<Blob> {
    return this.fetchPdf(invoiceId, false, false);
  }

  /**
   * Regenerate the invoice PDF and return it — `GET /invoices/{id}/pdf?recreate=true`.
   * `updateAddress` picks the customer's current billing address instead of the one
   * the invoice was issued with. There is no separate recreate route (EB-4396; the
   * former `/recreatePdf` and `/recreatePdfCurrentAddress` POSTs 404 at route level).
   */
  recreatePdf(invoiceId: number, updateAddress: boolean): Observable<Blob> {
    return this.fetchPdf(invoiceId, true, updateAddress);
  }

  private fetchPdf(invoiceId: number, recreate: boolean, updateAddress: boolean): Observable<Blob> {
    const params = new HttpParams().set('recreate', String(recreate)).set('updateAddress', String(updateAddress));
    return this.http.get(`${environment.invoiceUrl}/invoices/${invoiceId}/pdf`, { responseType: 'blob', params });
  }

  /**
   * Generate a credit note for an invoice. The server creates a **new preliminary
   * invoice** (`isCreditNote: true`) and returns it (EB-4396 — the route is
   * `/generatecredit`; the former `/credit` does not exist).
   */
  generateCredit(invoiceId: number): Observable<Invoice> {
    return this.http
      .post<Parameters<typeof this.mapV1Invoice>[0]>(`${environment.invoiceUrl}/invoices/${invoiceId}/generatecredit`, {})
      .pipe(map(this.mapV1Invoice));
  }

  /**
   * Delete a preliminary invoice / credit note (`DELETE /invoices/{id}`, text response).
   * invoice-api: "Only preliminary invoices can be deleted." (EB-4396, pinguin-invoice parity)
   */
  deleteInvoice(invoiceId: number): Observable<void> {
    return this.http
      .delete(`${environment.invoiceUrl}/invoices/${invoiceId}`, { responseType: 'text' })
      .pipe(map(() => undefined));
  }

  /**
   * Turn a preliminary invoice into a final one — `PUT /invoices/{id}/associatenumber`.
   * invoice-api: "Turn a preliminary invoice into a final invoice. Then send it to be
   * printed." Irreversible. Returns the now-numbered invoice.
   */
  turnToFinal(invoiceId: number): Observable<Invoice> {
    return this.http
      .put<Parameters<typeof this.mapV1Invoice>[0]>(`${environment.invoiceUrl}/invoices/${invoiceId}/associatenumber`, {})
      .pipe(map(this.mapV1Invoice));
  }

  /** Regenerate an invoice — the server creates and returns a **new preliminary invoice**. */
  regenerateInvoice(invoiceId: number): Observable<Invoice> {
    return this.http
      .post<Parameters<typeof this.mapV1Invoice>[0]>(`${environment.invoiceUrl}/invoices/${invoiceId}/regenerate`, {})
      .pipe(map(this.mapV1Invoice));
  }
}
